<template>
  <div class="relative overflow-hidden pb-16 md:pb-30">
    <div
      class="pointer-events-none absolute inset-x-0 top-0 flex justify-center"
    >
      <img
        src="/images/background-flower.svg"
        alt=""
        class="w-[760px] max-w-none opacity-20 md:w-[1000px] lg:w-[1150px]"
      />
    </div>

    <DefaultGrid class="relative z-2 gap-y-12 pt-32 md:pt-36">
      <div class="col-span-full text-center">
        <h1 class="type-heading-1 type-font-salt mx-auto max-w-3xl">
          Let kids be kids<br />for longer
        </h1>
        <p
          class="mx-auto max-w-xl py-8 text-lg font-normal md:py-12 md:text-xl"
        >
          Join parents and carers across Australia choosing to wait as long as
          possible before giving their kids a smartphone.
        </p>
        <div>
          <HomeSchoolSearch />
        </div>
      </div>

      <div class="col-span-full">
        <div
          class="flex flex-col sm:grid sm:grid-cols-2 sm:gap-4 lg:grid-cols-4"
        >
          <HomeStatCard
            class="bg-pink aspect-337/272 p-8 transition-transform duration-300 hover:z-20 hover:scale-110 hover:rotate-4 sm:aspect-square lg:p-5 xl:p-8"
            label="Wait Mate supporters"
            :stat="stats?.pledges"
          >
            <div v-if="stats?.latestPledge?.school" class="relative">
              <!-- cards behind (deck effect) -->
              <div
                class="absolute inset-x-4 top-3 -bottom-[10px] rounded-2xl bg-[#f7a7cd]"
              />
              <div
                class="absolute inset-x-2 top-1.5 -bottom-[5px] rounded-2xl bg-[#f9b3d5]"
              />
              <!-- front card -->
              <Transition :css="false" @enter="onEnter" @leave="onLeave">
                <div
                  :key="pledgeKey"
                  class="relative flex items-center gap-2.5 rounded-2xl bg-[#fbcde3] p-2.5"
                >
                  <img
                    src="/images/pledge-supporter-avatar.svg"
                    alt=""
                    class="size-10 flex-none"
                  />
                  <div class="min-w-0 text-[13px]/[1.25em]">
                    <p class="font-medium">New commitment at</p>
                    <p class="truncate opacity-50">
                      {{ stats.latestPledge.school }}
                    </p>
                  </div>
                </div>
              </Transition>
            </div>
          </HomeStatCard>

          <div
            class="relative z-10 -my-4 aspect-[359/300] -rotate-3 transition-transform duration-300 hover:z-20 hover:scale-110 hover:-rotate-6 sm:z-auto sm:my-0 sm:aspect-square sm:rotate-0 sm:overflow-hidden sm:rounded-[20px] lg:rotate-[-3deg]"
          >
            <picture>
              <!-- Mobile uses a landscape crop (matching the design) shown in full;
                   tablet/desktop use the square crop that fills the square cell. -->
              <source
                media="(max-width: 639px)"
                srcset="/images/girl-boy-play-at-home-wide.png"
              />
              <img
                src="/images/girl-boy-play-at-home.png"
                alt=""
                class="h-full w-full object-contain sm:object-cover"
              />
            </picture>
          </div>

          <HomeStatCard
            class="bg-blue aspect-337/272 p-8 transition-transform duration-300 hover:z-20 hover:scale-110 hover:-rotate-4 sm:order-last sm:aspect-square lg:order-0 lg:p-5 xl:p-8"
            label="Active schools"
            :stat="stats?.schools"
          >
            <NuxtLink
              to="/school-stats"
              class="btn btn-sm btn-outline w-full justify-center gap-2.5 whitespace-nowrap lg:gap-1.5 lg:text-sm xl:gap-2.5"
            >
              <span>View school stats</span>
              <SvgGlyphArrowRight class="h-5 w-5" />
            </NuxtLink>
          </HomeStatCard>

          <div
            class="hidden aspect-square overflow-hidden rounded-[20px] transition-transform duration-300 hover:z-20 hover:scale-110 hover:rotate-6 sm:block lg:rotate-3"
          >
            <img
              src="/images/3-girls-lying-on-grass.png"
              alt=""
              class="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    </DefaultGrid>
  </div>
</template>

<script setup lang="ts">
import type { Stats } from '@/components/global-stats/types'

interface Props {
  stats: Stats | null
}

const props = defineProps<Props>()

const { onEnter, onLeave } = usePledgeCardSwap()

const pledgeKey = computed(() => props.stats?.latestPledge?.school ?? '')
</script>
